import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Typography, Paper, Tabs, Tab, Table, TableBody, TableCell, TableHead, TableRow,
  Button, CircularProgress, Alert, Chip, Grid, Card, CardContent, Snackbar,
  FormControl, InputLabel, Select, MenuItem, TextField, IconButton,
  Dialog, DialogTitle, DialogContent, DialogActions, Tooltip
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import SearchIcon from '@mui/icons-material/Search';
import VisibilityIcon from '@mui/icons-material/Visibility';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import SendIcon from '@mui/icons-material/Send';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import BuildIcon from '@mui/icons-material/Build';
import ReplayIcon from '@mui/icons-material/Replay';
import { validationService } from '../services/validationService';
import { qualityThresholdService } from '../services/qualityThresholdService';
import { sourceSystemService } from '../services/sourceSystemService';

const severityColors = { critical: 'error', high: 'error', medium: 'warning', low: 'info' };
const statusColors = {
  pending: 'default', running: 'warning', passed: 'success', failed: 'error',
  reviewed_approved: 'success', reviewed_rejected: 'error', submitted_to_dve: 'success'
};

function ValidationDashboardIngestion() {
  const navigate = useNavigate();
  const [tabValue, setTabValue] = useState(0);
  const [sessions, setSessions] = useState([]);
  const [totalSessions, setTotalSessions] = useState(0);
  const [sourceSystems, setSourceSystems] = useState([]);
  const [thresholds, setThresholds] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  // Filters
  const [filters, setFilters] = useState({ search: '', overall_status: '', source_system_id: '', file_type: '' });

  // Detail dialog
  const [detailOpen, setDetailOpen] = useState(false);
  const [selectedSession, setSelectedSession] = useState(null);
  const [findings, setFindings] = useState([]);
  const [findingsTotal, setFindingsTotal] = useState(0);


  // Review dialog
  const [reviewOpen, setReviewOpen] = useState(false);
  const [reviewNotes, setReviewNotes] = useState('');

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    loadSessions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [thresholdsData, statsData, systemsData] = await Promise.all([
        qualityThresholdService.listAll(),
        validationService.getStatistics(),
        sourceSystemService.list()
      ]);
      setThresholds(thresholdsData || []);
      setStats(statsData);
      setSourceSystems(Array.isArray(systemsData) ? systemsData : systemsData.systems || []);
      await loadSessions();
    } catch (err) {
      setSnackbar({ open: true, message: 'Failed to load data', severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const loadSessions = async () => {
    try {
      const data = await validationService.listSessions({
        overall_status: filters.overall_status || undefined,
        source_system_id: filters.source_system_id || undefined,
        file_type: filters.file_type || undefined,
        search: filters.search || undefined,
        limit: 50
      });
      setSessions(data.sessions || []);
      setTotalSessions(data.total || 0);
    } catch (err) {
      console.error('Failed to load sessions:', err);
    }
  };

  const handleViewSession = async (session) => {
    try {
      const [detail, findingsData] = await Promise.all([
        validationService.getSession(session.session_id),
        validationService.getFindings(session.session_id, { limit: 100 })
      ]);
      setSelectedSession(detail);
      setFindings(findingsData.findings || []);
      setFindingsTotal(findingsData.total || 0);
      setDetailOpen(true);
    } catch (err) {
      setSnackbar({ open: true, message: 'Failed to load session details', severity: 'error' });
    }
  };

  const handleRerunValidation = async (session) => {
    try {
      setSnackbar({ open: true, message: 'Re-running validation...', severity: 'info' });
      await validationService.run(session.file_id, {
        mapping_template_id: session.mapping_template_id
      });
      setSnackbar({ open: true, message: 'Validation re-run complete', severity: 'success' });
      await loadData();
    } catch (err) {
      setSnackbar({ open: true, message: `Re-run failed: ${err.response?.data?.error || err.message}`, severity: 'error' });
    }
  };

  const handleReview = async (decision) => {
    if (!selectedSession) return;
    try {
      await validationService.reviewSession(selectedSession.session_id, decision, 'user', reviewNotes);
      setSnackbar({ open: true, message: `Session ${decision}d`, severity: 'success' });
      setReviewOpen(false);
      setReviewNotes('');
      setDetailOpen(false);
      loadSessions();
    } catch (err) {
      const errMsg = err.response?.data?.error;
      setSnackbar({ open: true, message: (typeof errMsg === 'string' ? errMsg : errMsg?.message) || 'Review failed', severity: 'error' });
    }
  };

  const handleAutoFix = async () => {
    if (!selectedSession) return;
    try {
      const result = await validationService.resolveAutoFixable(selectedSession.session_id, 'user');
      setSnackbar({ open: true, message: `${result.resolved_count} findings auto-resolved`, severity: 'success' });
      handleViewSession(selectedSession);
    } catch (err) {
      setSnackbar({ open: true, message: 'Auto-fix failed', severity: 'error' });
    }
  };

  const handleSubmitToDve = async () => {
    if (!selectedSession) return;
    try {
      await validationService.submitToDve(selectedSession.session_id);
      setSnackbar({ open: true, message: 'Submitted to dVE for verification', severity: 'success' });
      setDetailOpen(false);
      loadSessions();
    } catch (err) {
      const errMsg = err.response?.data?.error;
      setSnackbar({ open: true, message: (typeof errMsg === 'string' ? errMsg : errMsg?.message) || 'Submission failed', severity: 'error' });
    }
  };

  if (loading) {
    return <Box display="flex" justifyContent="center" p={4}><CircularProgress /></Box>;
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Box>
          <Typography variant="h4" gutterBottom>Quality & Validation</Typography>
          <Typography variant="body1" color="text.secondary">
            PACT DQI quality thresholds and pre-dVE data validation
          </Typography>
        </Box>
        <IconButton onClick={loadData}><RefreshIcon /></IconButton>
      </Box>

      {/* Stats Cards */}
      {stats && (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={6} sm={3}>
            <Card elevation={1}><CardContent>
              <Typography variant="subtitle2" color="text.secondary">Total Sessions</Typography>
              <Typography variant="h5" sx={{ fontWeight: 600 }}>{stats.total_sessions}</Typography>
            </CardContent></Card>
          </Grid>
          <Grid item xs={6} sm={3}>
            <Card elevation={1}><CardContent>
              <Typography variant="subtitle2" color="text.secondary">Records Validated</Typography>
              <Typography variant="h5" sx={{ fontWeight: 600 }}>{stats.total_records_validated}</Typography>
            </CardContent></Card>
          </Grid>
          <Grid item xs={6} sm={3}>
            <Card elevation={1}><CardContent>
              <Typography variant="subtitle2" color="text.secondary">Pass Rate</Typography>
              <Typography variant="h5" color="success.main" sx={{ fontWeight: 600 }}>
                {stats.total_records_validated > 0
                  ? `${((stats.total_passed / stats.total_records_validated) * 100).toFixed(1)}%` : '-'}
              </Typography>
            </CardContent></Card>
          </Grid>
          <Grid item xs={6} sm={3}>
            <Card elevation={1}><CardContent>
              <Typography variant="subtitle2" color="text.secondary">Avg Quality</Typography>
              <Typography variant="h5" sx={{ fontWeight: 600 }}>
                {stats.avg_quality_score != null ? `${(stats.avg_quality_score * 100).toFixed(1)}%` : '-'}
              </Typography>
            </CardContent></Card>
          </Grid>
        </Grid>
      )}

      <Paper sx={{ mb: 3 }}>
        <Tabs value={tabValue} onChange={(e, v) => setTabValue(v)}>
          <Tab label="Validation Sessions" />
          <Tab label="Quality Thresholds" />
        </Tabs>
      </Paper>

      {/* Tab 0: Sessions */}
      {tabValue === 0 && (
        <>
        {/* Filters */}
        <Paper sx={{ p: 2, mb: 2 }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={3}>
              <TextField fullWidth size="small" label="Search" placeholder="File name..."
                value={filters.search} onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                InputProps={{ startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} /> }}
              />
            </Grid>
            <Grid item xs={6} sm={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Status</InputLabel>
                <Select value={filters.overall_status} label="Status"
                  onChange={(e) => setFilters({ ...filters, overall_status: e.target.value })}>
                  <MenuItem value="">All</MenuItem>
                  {['pending', 'running', 'passed', 'failed', 'reviewed_approved', 'reviewed_rejected', 'submitted_to_dve'].map(s =>
                    <MenuItem key={s} value={s}>{s.replace(/_/g, ' ')}</MenuItem>
                  )}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6} sm={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Source System</InputLabel>
                <Select value={filters.source_system_id} label="Source System"
                  onChange={(e) => setFilters({ ...filters, source_system_id: e.target.value })}>
                  <MenuItem value="">All</MenuItem>
                  {sourceSystems.map(s => <MenuItem key={s.source_system_id} value={s.source_system_id}>{s.system_name}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6} sm={2}>
              <FormControl fullWidth size="small">
                <InputLabel>File Type</InputLabel>
                <Select value={filters.file_type} label="File Type"
                  onChange={(e) => setFilters({ ...filters, file_type: e.target.value })}>
                  <MenuItem value="">All</MenuItem>
                  <MenuItem value="csv">CSV</MenuItem>
                  <MenuItem value="json">JSON</MenuItem>
                  <MenuItem value="xlsx">XLSX</MenuItem>
                  <MenuItem value="parquet">Parquet</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </Paper>

        <Paper sx={{ p: 2 }}>
          <Typography variant="h6" gutterBottom>Sessions ({totalSessions})</Typography>
          {sessions.length === 0 ? (
            <Alert severity="info">No validation sessions found. Run validation from the AI Mapping wizard.</Alert>
          ) : (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>File</TableCell>
                  <TableCell>Validation</TableCell>
                  <TableCell>Records</TableCell>
                  <TableCell>Passed</TableCell>
                  <TableCell>Failed</TableCell>
                  <TableCell>Quality Score</TableCell>
                  <TableCell>Quality Gate</TableCell>
                  <TableCell>Created</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {sessions.map((s) => {
                  const isAutoReject = s.quality_classification === 'auto_reject';
                  const isRequiresReview = s.quality_classification === 'requires_review';
                  return (
                    <TableRow key={s.session_id} hover
                      sx={{
                        bgcolor: isAutoReject ? 'rgba(211,47,47,0.06)' :
                          isRequiresReview ? 'rgba(237,108,2,0.06)' : 'inherit'
                      }}>
                      <TableCell>
                        <Typography variant="body2" noWrap sx={{ maxWidth: 200 }}>
                          {s.file?.original_name || s.file?.file_name || s.file_id?.substring(0, 8)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip label={(s.overall_status || 'unknown').replace(/_/g, ' ')} size="small"
                          color={statusColors[s.overall_status] || 'default'} />
                      </TableCell>
                      <TableCell>{s.total_records}</TableCell>
                      <TableCell sx={{ color: 'success.main' }}>{s.passed_records}</TableCell>
                      <TableCell sx={{ color: s.failed_records > 0 ? 'error.main' : 'inherit' }}>{s.failed_records}</TableCell>
                      <TableCell>
                        {s.quality_score != null ? (
                          <Typography variant="body2" sx={{
                            fontWeight: 600,
                            color: s.quality_classification === 'auto_accept' ? 'success.main' :
                              s.quality_classification === 'auto_reject' ? 'error.main' : 'warning.main'
                          }}>
                            {(Number(s.quality_score) * 100).toFixed(1)}%
                          </Typography>
                        ) : '-'}
                      </TableCell>
                      <TableCell>
                        {s.quality_classification ? (
                          <Tooltip title={
                            isAutoReject
                              ? `Score ${(Number(s.quality_score) * 100).toFixed(1)}% is below the auto-reject threshold. Data was NOT written to OFP. Fix OFP mappings on sensors (facility, emission activity, equipment) to improve coverage score.`
                              : isRequiresReview
                              ? `Score ${(Number(s.quality_score) * 100).toFixed(1)}% requires manual review before OFP recording.`
                              : `Score ${(Number(s.quality_score) * 100).toFixed(1)}% — data accepted and written to OFP.`
                          }>
                            <Chip
                              icon={isAutoReject ? <WarningAmberIcon /> : undefined}
                              label={(s.quality_classification || 'unknown').replace(/_/g, ' ')}
                              size="small"
                              color={
                                s.quality_classification === 'auto_accept' ? 'success' :
                                s.quality_classification === 'auto_reject' ? 'error' : 'warning'
                              }
                            />
                          </Tooltip>
                        ) : '-'}
                      </TableCell>
                      <TableCell>{new Date(s.created_at).toLocaleDateString()}</TableCell>
                      <TableCell>
                        <IconButton size="small" onClick={() => handleViewSession(s)} title="View details">
                          <VisibilityIcon fontSize="small" />
                        </IconButton>
                        <Tooltip title="Re-run validation">
                          <IconButton size="small" color="primary"
                            onClick={() => handleRerunValidation(s)}>
                            <ReplayIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        {isAutoReject && (
                          <Tooltip title="Fix OFP mappings: assign facility, emission activity, and equipment to sensors">
                            <IconButton size="small" color="warning"
                              onClick={() => navigate('/source-systems')}>
                              <BuildIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </Paper>
        </>
      )}

      {/* Tab 1: Thresholds */}
      {tabValue === 1 && (
        <Paper sx={{ p: 2 }}>
          <Typography variant="h6" gutterBottom>Quality Thresholds by Source System Type</Typography>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Source Type</TableCell>
                <TableCell>Auto-Accept (&ge;)</TableCell>
                <TableCell>Auto-Reject (&lt;)</TableCell>
                <TableCell>Review Zone</TableCell>
                <TableCell>Default?</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {thresholds.map((t) => (
                <TableRow key={t.source_system_type}>
                  <TableCell><Chip label={t.source_system_type} size="small" variant="outlined" /></TableCell>
                  <TableCell>
                    <Chip label={`${(Number(t.auto_accept_threshold) * 100).toFixed(0)}%`} size="small" color="success" />
                  </TableCell>
                  <TableCell>
                    <Chip label={`${(Number(t.auto_reject_threshold) * 100).toFixed(0)}%`} size="small" color="error" />
                  </TableCell>
                  <TableCell>
                    {(Number(t.auto_reject_threshold) * 100).toFixed(0)}% - {(Number(t.auto_accept_threshold) * 100).toFixed(0)}%
                  </TableCell>
                  <TableCell>{t.is_default ? <Chip label="Default" size="small" /> : <Chip label="Custom" size="small" color="primary" />}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Paper>
      )}

      {/* Session Detail Dialog */}
      <Dialog open={detailOpen} onClose={() => setDetailOpen(false)} maxWidth="lg" fullWidth>
        {selectedSession && (
          <>
            <DialogTitle>
              Validation Session
              <Chip label={selectedSession.overall_status?.replace(/_/g, ' ')} size="small" sx={{ ml: 1 }}
                color={statusColors[selectedSession.overall_status] || 'default'} />
            </DialogTitle>
            <DialogContent>
              <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid item xs={3}>
                  <Typography variant="subtitle2" color="text.secondary">Total Records</Typography>
                  <Typography variant="h6">{selectedSession.total_records}</Typography>
                </Grid>
                <Grid item xs={3}>
                  <Typography variant="subtitle2" color="text.secondary">Passed</Typography>
                  <Typography variant="h6" color="success.main">{selectedSession.passed_records}</Typography>
                </Grid>
                <Grid item xs={3}>
                  <Typography variant="subtitle2" color="text.secondary">Failed</Typography>
                  <Typography variant="h6" color="error.main">{selectedSession.failed_records}</Typography>
                </Grid>
                <Grid item xs={3}>
                  <Typography variant="subtitle2" color="text.secondary">Quality</Typography>
                  <Typography variant="h6">
                    {selectedSession.quality_score != null
                      ? `${(Number(selectedSession.quality_score) * 100).toFixed(1)}%` : '-'}
                  </Typography>
                </Grid>
              </Grid>

              {/* Findings */}
              <Typography variant="h6" gutterBottom>Findings ({findingsTotal})</Typography>
              {findings.length === 0 ? (
                <Alert severity="success">No validation findings - all checks passed.</Alert>
              ) : (
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Row</TableCell>
                      <TableCell>Field</TableCell>
                      <TableCell>Validator</TableCell>
                      <TableCell>Severity</TableCell>
                      <TableCell>Message</TableCell>
                      <TableCell>Fixable</TableCell>
                      <TableCell>Resolved</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {findings.slice(0, 50).map((f) => (
                      <TableRow key={f.finding_id}>
                        <TableCell>{f.record_index}</TableCell>
                        <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.85rem' }}>{f.field_name}</TableCell>
                        <TableCell><Chip label={f.validator_type} size="small" variant="outlined" /></TableCell>
                        <TableCell><Chip label={f.severity} size="small" color={severityColors[f.severity] || 'default'} /></TableCell>
                        <TableCell>
                          <Typography variant="body2" noWrap sx={{ maxWidth: 300 }}>{f.message}</Typography>
                        </TableCell>
                        <TableCell>{f.auto_fixable ? <Chip label="Yes" size="small" color="info" /> : '-'}</TableCell>
                        <TableCell>{f.resolved ? <CheckCircleIcon color="success" fontSize="small" /> : '-'}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </DialogContent>
            <DialogActions>
              {findings.some(f => f.auto_fixable && !f.resolved) && (
                <Button startIcon={<AutoFixHighIcon />} onClick={handleAutoFix} color="info">Auto-Fix</Button>
              )}
              {['failed', 'passed'].includes(selectedSession.overall_status) && (
                <Button startIcon={<CheckCircleIcon />} onClick={() => setReviewOpen(true)} color="primary">Review</Button>
              )}
              {['passed', 'reviewed_approved'].includes(selectedSession.overall_status) && (
                <Button startIcon={<SendIcon />} onClick={handleSubmitToDve} variant="contained" color="success">
                  Submit to dVE
                </Button>
              )}
              <Button onClick={() => setDetailOpen(false)}>Close</Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* Review Dialog */}
      <Dialog open={reviewOpen} onClose={() => setReviewOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Review Validation Session</DialogTitle>
        <DialogContent>
          <TextField fullWidth multiline rows={3} label="Review Notes" value={reviewNotes}
            onChange={(e) => setReviewNotes(e.target.value)} sx={{ mt: 1 }} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReviewOpen(false)}>Cancel</Button>
          <Button startIcon={<CancelIcon />} onClick={() => handleReview('reject')} color="error">Reject</Button>
          <Button startIcon={<CheckCircleIcon />} onClick={() => handleReview('approve')} color="success" variant="contained">Approve</Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={snackbar.open} autoHideDuration={6000} onClose={() => setSnackbar({ ...snackbar, open: false })}>
        <Alert severity={snackbar.severity}>{snackbar.message}</Alert>
      </Snackbar>
    </Box>
  );
}

export default ValidationDashboardIngestion;
